import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  Sparkles, 
  Trophy, 
  Target,
  Zap,
  ArrowRight
} from 'lucide-react';
import React from 'react';
import { useState } from 'react';

import type { OnboardingStepProps } from './onboardingTypes';

// Celebration Animation Component
const CelebrationAnimation: React.FC = () => {
  // Generate confetti particles
  const confettiParticles = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: -10,
    rotation: Math.random() * 360,
    color: ['#22D3EE', '#A855F7', '#10B981', '#F59E0B', '#EF4444'][Math.floor(Math.random() * 5)],
    size: Math.random() * 8 + 4,
    delay: Math.random() * 2
  }));

  return (
    <motion.div
      className="fixed inset-0 z-[10001] pointer-events-none overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Confetti Particles */}
      {confettiParticles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full"
          style={{
            left: `${particle.x}%`,
            backgroundColor: particle.color,
            width: particle.size,
            height: particle.size
          }}
          initial={{ y: -10, rotate: 0 }}
          animate={{ 
            y: window.innerHeight + 50, 
            rotate: particle.rotation 
          }}
          transition={{ 
            duration: 3, 
            delay: particle.delay,
            ease: "easeOut"
          }}
        />
      ))}
    </motion.div>
  );
};

const OnboardingStep4_Completion: React.FC<OnboardingStepProps> = ({
  data,
  onNext,
  isLoading,
  userPersona
}) => {
  const [showCelebration, setShowCelebration] = useState(false);

  const handleComplete = () => {
    // Immediately proceed to next step (which will trigger completion)
    onNext();
  };

  return (
    <>
      <div className="text-center space-y-6">
        {/* Success Icon */}
        <motion.div
          className="flex justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          <div className="w-20 h-20 bg-gradient-to-br from-green-500/20 to-cyan-500/20 rounded-full flex items-center justify-center border-2 border-green-500/30">
            <Trophy className="w-10 h-10 text-green-400" />
          </div>
        </motion.div>

        {/* Completion Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h2 className="text-2xl font-bold vscode-text-primary mb-3">
            ?? Setup Complete!
          </h2>
          <p className="vscode-text-secondary text-lg">
            Welcome to GoGrabJob, {data.industry ? `${data.industry} professional` : 'career seeker'}!
          </p>
        </motion.div>

        {/* Summary */}
        <motion.div
          className="vscode-bg-secondary/50 rounded-lg p-6 border vscode-border"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <h3 className="text-lg font-semibold vscode-text-primary mb-4">Your Profile Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
            {data.industry && (
              <div className="flex items-center space-x-3">
                <Target className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="vscode-text-tertiary text-sm">Industry</p>
                  <p className="vscode-text-primary font-medium">{data.industry}</p>
                </div>
              </div>
            )}
            
            {data.experienceLevel && (
              <div className="flex items-center space-x-3">
                <Zap className="w-5 h-5 text-purple-400" />
                <div>
                  <p className="vscode-text-tertiary text-sm">Experience Level</p>
                  <p className="vscode-text-primary font-medium">{data.experienceLevel}</p>
                </div>
              </div>
            )}
            
            {data.careerGoals && data.careerGoals.length > 0 && (
              <div className="flex items-center space-x-3 md:col-span-2">
                <Sparkles className="w-5 h-5 text-green-400" />
                <div>
                  <p className="vscode-text-tertiary text-sm">Career Goals</p>
                  <p className="vscode-text-primary font-medium">
                    {data.careerGoals.slice(0, 2).join(', ')}
                    {data.careerGoals.length > 2 && ` +${data.careerGoals.length - 2} more`}
                  </p>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Next Steps */}
        <motion.div
          className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-lg p-6 border border-cyan-500/30"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <h3 className="text-lg font-semibold vscode-text-primary mb-3">What's Next?</h3>
          <div className="space-y-2 text-left">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="vscode-text-secondary">Upload your resume</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="vscode-text-secondary">Add job URLs you're interested in</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="vscode-text-secondary">Let AI optimize your resume</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="vscode-text-secondary">Download and apply with confidence</span>
            </div>
          </div>
        </motion.div>

        {/* Complete Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <button
            onClick={handleComplete}
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 vscode-text-primary font-semibold py-4 px-6 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center space-x-2 shadow-xl hover:shadow-cyan-500/30"
          >
            {isLoading ? (
              <>
                <motion.div
                  className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                />
                <span>Setting up your dashboard...</span>
              </>
            ) : (
              <>
                <span>Complete Setup</span>
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </motion.div>
      </div>

      {/* Celebration Animation */}
      {showCelebration && <CelebrationAnimation />}
    </>
  );
};

export default React.memo(OnboardingStep4_Completion);
